import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';

function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

const NOTIFICATION_TYPES = ['maintenance', 'error', 'announcement', 'feature', 'user_alert', 'billing'];
const SEVERITY_LEVELS = ['info', 'success', 'warning', 'error', 'critical'];

const severityColors = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
  critical: 'error',
};

function AdminNotifications() {
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  // Form state
  const [target, setTarget] = useState('all');
  const [type, setType] = useState('announcement');
  const [severity, setSeverity] = useState('info');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [actionText, setActionText] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const debouncedUserSearch = useDebouncedValue(userSearch, 300);

  const processedFilter = tab === 0 ? 'pending' : 'all';

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['admin-notifications', processedFilter],
    queryFn: () => api.get('/api/admin/notifications', { page: 1, limit: 100, processedFilter }),
    refetchInterval: tab === 0 ? 15000 : false,
  });

  // Search users for autocomplete
  const { data: userSearchData } = useQuery({
    queryKey: ['admin-users-search', debouncedUserSearch],
    queryFn: () => api.get('/api/admin/users', { page: 1, limit: 10, search: debouncedUserSearch }),
    enabled: target === 'users' && debouncedUserSearch.length > 1,
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/admin/notifications', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
      setTitle('');
      setMessage('');
      setActionUrl('');
      setActionText('');
      setSelectedUsers([]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    },
  });

  const notifications = notificationsData?.notifications || [];
  const userOptions = userSearchData?.users || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;

    createMutation.mutate({
      target,
      userIds: target === 'users' ? selectedUsers.map((u) => u.email || u._id) : undefined,
      type,
      severity,
      title: title.trim(),
      message: message.trim(),
      actionUrl: actionUrl.trim() || undefined,
      actionText: actionText.trim() || undefined,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Notifications</Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Create Form */}
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Create Notification</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="users">Specific Users</MenuItem>
              </TextField>

              {target === 'users' && (
                <Autocomplete
                  multiple
                  options={userOptions}
                  getOptionLabel={(option) => `${option.name || ''} (${option.email})`}
                  value={selectedUsers}
                  onChange={(e, val) => setSelectedUsers(val)}
                  inputValue={userSearch}
                  onInputChange={(e, val) => setUserSearch(val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Search Users" size="small" placeholder="Type to search..." />
                  )}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  loading={!!debouncedUserSearch && !userSearchData}
                  filterOptions={(x) => x}
                />
              )}

              <TextField
                select
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                size="small"
              >
                {NOTIFICATION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                size="small"
              >
                {SEVERITY_LEVELS.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                size="small"
                slotProps={{
                  htmlInput: { maxLength: 100 }
                }}
              />

              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                multiline
                rows={3}
                size="small"
                slotProps={{
                  htmlInput: { maxLength: 1000 }
                }}
              />

              <TextField
                label="Action URL (optional)"
                value={actionUrl}
                onChange={(e) => setActionUrl(e.target.value)}
                size="small"
              />

              <TextField
                label="Action Text (optional)"
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                size="small"
                slotProps={{
                  htmlInput: { maxLength: 50 }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={createMutation.isPending || !title.trim() || !message.trim()}
              >
                {createMutation.isPending ? 'Sending...' : 'Send Notification'}
              </Button>

              {createMutation.isError && (
                <Alert severity="error">{createMutation.error?.message || 'Failed to create notification'}</Alert>
              )}
              {createMutation.isSuccess && (
                <Alert severity="success">Notification created</Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Notifications List */}
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Paper sx={{ p: 2 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Pending" />
              <Tab label="All" />
            </Tabs>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : notifications.length === 0 ? (
              <Typography
                sx={{
                  color: "text.secondary",
                  py: 2,
                  textAlign: 'center'
                }}>
                No notifications
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((n) => (
                      <TableRow key={n._id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {n.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={n.type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={n.severity} size="small" color={severityColors[n.severity] || 'default'} />
                        </TableCell>
                        <TableCell>
                          {n.target === 'all' ? 'All' : `${n.userIds?.length || 0} users`}
                        </TableCell>
                        <TableCell>
                          {dayjs(n.createdAt).format('MMM D h:mm A')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={n.processed ? 'Processed' : 'Pending'}
                            color={n.processed ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {!n.processed && (
                            <IconButton
                              size="small"
                              onClick={() => deleteMutation.mutate(n._id)}
                              disabled={deleteMutation.isPending}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminNotifications;
